import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import {
   DirectoryValuesRemoveEntry,
   DirectoryValuesUpdateEntry,
   SetLoadedDirectoryValues,
   SetSearchExecuted,
   SetSearchRelatedData,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-data/directory-data-behaviour.actions';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { DirectoryRestrictionFilterRemoteDto } from 'src/generated/swagger';
import { DirectorySettingsState } from '../../../../brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import {
   ClearAndStartLoadingAllDirectoryValues,
   ClearAndStartLoadingDirectoryValues,
   DirectoryBrowserChangeMatchingPercentage,
   DirectoryBrowserEntrySelected,
   DirectoryCallEnded,
   DirectoryNewIncomingCall,
   DirectorySearchValueChanged,
   EditEntryInAllDirectories,
   EmptyDirectory,
   LoadMoreDirectoryValues,
   RemoveEntryFromAllDirectories,
   RemoveUnusedSelectedEntries,
   ResetDirectorySearch,
   SetIsCallTriggeredDirectoryAssociationsFilterActive,
   SetIsCallerFilterActive,
   SetSearchValue,
   SetTemporarySearchColumns,
   ToggleDirectoryFilterForCaller,
   ToggleDirectoryFilterForIncomingCall,
} from './directory-browser-behaviour.actions';

export interface DirectoryBrowserBehaviourModel {
   [brickInstanceId: string]: DirectoryBrowserBehaviourInstance;
}

export interface DirectoryBrowserBehaviourInstance {
   filterForSubscriberId: number;
   callTriggeredDirectoryAssociations: DirectoryRestrictionFilterRemoteDto[];
   isCallTriggeredDirectoryAssociationsFilterActive: boolean;
   isCallerFilterActive: boolean;
   searchValue: string;
   temporarySearchColumns: DirectoryColumn[];
   selectedDirectory: number;
   matchingPercentage: number;
}

export function defaultDirectoryBehaviourInstance(): DirectoryBrowserBehaviourInstance {
   return {
      filterForSubscriberId: null,
      callTriggeredDirectoryAssociations: null,
      isCallTriggeredDirectoryAssociationsFilterActive: false,
      isCallerFilterActive: false,
      searchValue: null,
      temporarySearchColumns: [],
      selectedDirectory: null,
      matchingPercentage: 100,
   };
}

@State<DirectoryBrowserBehaviourModel>({
   name: 'directoryBrowserBehaviour',
   defaults: {},
})
@Injectable()
export class DirectoryBrowserBehaviourState {
   constructor(private store: Store) {}

   @Selector([DirectoryBrowserBehaviourState])
   public static getSpecificBehaviourStateOrDefault(state: DirectoryBrowserBehaviourModel) {
      return (brickInstanceId: string) => {
         return DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getSearchValue(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).searchValue;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSearchValue])
   public static getSearchValuesSplitted(getDirectorySearchValueForBrick: (brickInstanceId: string) => string) {
      return (brickInstanceId: string) => {
         return _.split(getDirectorySearchValueForBrick(brickInstanceId), ' ').filter((searchValue) => !_.isEmpty(searchValue)) ?? [];
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getTemporarySearchColumns(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).temporarySearchColumns;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getIsCallerFilterActive(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).isCallerFilterActive;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getCallTriggeredDirectoryAssociations(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).callTriggeredDirectoryAssociations;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getIsCallTriggeredDirectoryAssociationsFilterActive(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).isCallTriggeredDirectoryAssociationsFilterActive;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getSelectedDirectoryID(specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourState(brickInstanceId).selectedDirectory;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getDirectoryMatchingPercentage(directoryBrowserBehaviourForBrick: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return directoryBrowserBehaviourForBrick(brickInstanceId).matchingPercentage;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getFilterForSubscriberId(directoryBrowserBehaviourForBrick: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return directoryBrowserBehaviourForBrick(brickInstanceId).filterForSubscriberId;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSelectedDirectoryID])
   public static getIsEntrySelected(getSelectedDirectoryID: (brickInstanceId: string) => number) {
      return (brickInstanceId: string) => {
         return !_.isNil(getSelectedDirectoryID(brickInstanceId)) ? true : false;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSelectedDirectoryID, DirectoryBrowserBehaviourState.getIsEntrySelected])
   public static getSelectedDirectoryIDs(
      getSelectedDirectoryID: (brickInstanceId: string) => number,
      getIsEntrySelected: (brickInstanceId: string) => boolean
   ) {
      return (brickInstanceId: string) => {
         return getIsEntrySelected(brickInstanceId) ? [getSelectedDirectoryID(brickInstanceId)] : [];
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getDirectoryMatchingPercentage])
   public static getIsSearchPhonetic(getMatchingPercentage: (brickInstanceId: string) => number) {
      return (brickInstanceId: string) => {
         return getMatchingPercentage(brickInstanceId) < 100;
      };
   }

   private static getSpecificBehaviourStateOrDefaultInternal(
      state: DirectoryBrowserBehaviourModel,
      brickInstanceId: string
   ): DirectoryBrowserBehaviourInstance {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return defaultDirectoryBehaviourInstance();
      }
   }

   @Action(SetSearchValue)
   @ImmutableContext()
   public setSearchValue(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: SetSearchValue) {
      const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      if (directoryState.searchValue === action.searchValue) {
         return;
      }
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         directoryState.searchValue = action.searchValue;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetIsCallerFilterActive)
   @ImmutableContext()
   public setIsCallerFilterActive(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: SetIsCallerFilterActive) {
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.isCallerFilterActive = action.isCallerFilterActive;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetTemporarySearchColumns)
   @ImmutableContext()
   public setTemporarySearchColumns(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: SetTemporarySearchColumns) {
      const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      if (_.isEqual(directoryState.temporarySearchColumns, action.temporarySearchColumns)) {
         return;
      }
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         directoryState.temporarySearchColumns = action.temporarySearchColumns;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetIsCallTriggeredDirectoryAssociationsFilterActive)
   @ImmutableContext()
   public setIsCallTriggeredDirectoryAssociationsFilterActive(
      ctx: StateContext<DirectoryBrowserBehaviourModel>,
      action: SetIsCallTriggeredDirectoryAssociationsFilterActive
   ) {
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.isCallTriggeredDirectoryAssociationsFilterActive = action.isOrganisationAssociationsFilterActive;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
      return ctx.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   @Action(DirectoryBrowserEntrySelected)
   @ImmutableContext()
   public directoryBrowserEntrySelected(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserEntrySelected) {
      const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      if (directoryState.selectedDirectory === action.directoryId) {
         return;
      }
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         directoryState.selectedDirectory = action.directoryId;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(DirectoryBrowserChangeMatchingPercentage)
   @ImmutableContext()
   public directoryBrowserChangeMatchingPercentage(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserChangeMatchingPercentage) {
      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.matchingPercentage = action.matchingPercentage;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(ResetDirectorySearch)
   @ImmutableContext()
   public resetDirectorySearch(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: ResetDirectorySearch) {
      ctx.dispatch(new SetLoadedDirectoryValues(action.brickInstanceId, []));
      ctx.dispatch(new SetSearchExecuted(action.brickInstanceId, false));
   }

   @Action(RemoveEntryFromAllDirectories)
   @ImmutableContext()
   public removeEntryFromAllDirectories(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: RemoveEntryFromAllDirectories) {
      const state = ctx.getState();
      for (const key in state) {
         if (!_.isNil(key)) {
            ctx.dispatch(new DirectoryValuesRemoveEntry(key, action.id));
         }
      }
   }

   @Action(EditEntryInAllDirectories)
   @ImmutableContext()
   public editEntryInAllDirectories(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: EditEntryInAllDirectories) {
      const state = ctx.getState();
      for (const key in state) {
         if (!_.isNil(key)) {
            ctx.dispatch(new DirectoryValuesUpdateEntry(key, action.id, action.entry));
         }
      }
   }

   @Action(ClearAndStartLoadingAllDirectoryValues)
   @ImmutableContext()
   public clearAndStartLoadingAllDirectoryValues(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: ClearAndStartLoadingAllDirectoryValues) {
      const state = ctx.getState();
      for (const key in state) {
         if (key !== action.brickInstanceId) {
            ctx.dispatch(new ClearAndStartLoadingDirectoryValues(key));
         }
      }
   }

   @Action(RemoveUnusedSelectedEntries)
   @ImmutableContext()
   public removeUnusedSelectedEntries(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: RemoveUnusedSelectedEntries) {
      const state = ctx.getState();
      for (const key in state) {
         if (key !== 'directoryEntriesInEditing') {
            if (key !== action.brickInstanceId) {
               ctx.dispatch(new DirectoryBrowserEntrySelected(key, null));
            }
         }
      }
   }

   @Action(EmptyDirectory)
   @ImmutableContext()
   public emptyDirectory(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: EmptyDirectory) {
      ctx.dispatch(new SetSearchRelatedData(action.brickInstanceId, [], false, false));
      ctx.dispatch(new DirectoryBrowserEntrySelected(action.brickInstanceId, null));
      ctx.dispatch(new SetSearchValue(action.brickInstanceId, ''));
   }

   @Action(ClearAndStartLoadingDirectoryValues)
   @ImmutableContext()
   public clearAndStartLoadingDirectoryValues(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: ClearAndStartLoadingDirectoryValues) {
      ctx.dispatch(new SetSearchRelatedData(action.brickInstanceId, [], false, false));
      ctx.dispatch(new DirectoryBrowserEntrySelected(action.brickInstanceId, null));
      ctx.dispatch(new LoadMoreDirectoryValues(action.brickInstanceId));
   }

   @Action(DirectorySearchValueChanged)
   @ImmutableContext()
   public directorySearchValueChanged(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectorySearchValueChanged) {
      if (!action.keepSelection) {
         ctx.dispatch(new DirectoryBrowserEntrySelected(action.brickInstanceId, null));
      }
      ctx.dispatch(new SetSearchValue(action.brickInstanceId, action.searchValue));
      ctx.dispatch(new SetIsCallerFilterActive(action.brickInstanceId, false));
      if (_.isEmpty(action.searchValue)) {
         ctx.dispatch(new SetTemporarySearchColumns(action.brickInstanceId, []));
      }

      ctx.dispatch(new SetSearchExecuted(action.brickInstanceId, false));
   }

   @Action(DirectoryNewIncomingCall)
   @ImmutableContext()
   public directoryNewIncomingCall(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryNewIncomingCall) {
      let callerFilterRequiresRefresh = false;
      let orgFilterRequiresRefresh = false;
      let clearSearchBoxRequiresRefresh = false;

      ctx.setState((state: DirectoryBrowserBehaviourModel) => {
         const newDirectoryState = DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);

         callerFilterRequiresRefresh = this.handleFilterForCaller(newDirectoryState, action);
         orgFilterRequiresRefresh = this.handleOrganizationAssociationFilter(newDirectoryState, action);
         clearSearchBoxRequiresRefresh = this.handleClearSearchBoxSetting(newDirectoryState, action);

         state[action.brickInstanceId] = newDirectoryState;
         return state;
      });

      // refresh the grid in case any of the filters have caused a change
      if (orgFilterRequiresRefresh || callerFilterRequiresRefresh || clearSearchBoxRequiresRefresh) {
         return ctx.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
      }
   }

   @Action(DirectoryCallEnded)
   @ImmutableContext()
   public directoryCallEnded(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryCallEnded) {
      const orgAssociationFilterActive = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getIsCallTriggeredDirectoryAssociationsFilterActive)(
         action.brickInstanceId
      );
      const retainFilterOnCallFinish = this.store.selectSnapshot(DirectorySettingsState.getRetainFilterOnCallFinish)(action.brickInstanceId);
      if (!orgAssociationFilterActive || retainFilterOnCallFinish) {
         // nothing to do
         return;
      }

      return ctx.dispatch(new SetIsCallTriggeredDirectoryAssociationsFilterActive(action.brickInstanceId, false));
   }

   @Action(ToggleDirectoryFilterForIncomingCall)
   @ImmutableContext()
   public toggleDirectoryFilterForIncomingCall(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: ToggleDirectoryFilterForIncomingCall) {
      if (action.isFilteringPossible) {
         const state = DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId);
         ctx.dispatch(new SetIsCallTriggeredDirectoryAssociationsFilterActive(action.brickInstanceId, !state.isCallTriggeredDirectoryAssociationsFilterActive));
      }
      return ctx.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   @Action(ToggleDirectoryFilterForCaller)
   @ImmutableContext()
   public toggleDirectoryFilterForCaller(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: ToggleDirectoryFilterForCaller) {
      const directoryState = DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId);
      const newCallFilterActive = !directoryState.isCallerFilterActive;
      ctx.dispatch(new SetIsCallerFilterActive(action.brickInstanceId, newCallFilterActive));
      if (newCallFilterActive) {
         ctx.dispatch(new SetSearchValue(action.brickInstanceId, null));
         ctx.dispatch(new SetTemporarySearchColumns(action.brickInstanceId, []));
      }
      return ctx.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   /**
    * Helper function to handle action @see DirectoryNewIncomingCall
    * in regards to setting @method DirectorySettingsState.getAutomaticFilterOnSkillOrganisationAssociations
    * @param newDirectoryState Current state (to be modified while handling the action)
    * @returns true if directory has to be refreshed, else false
    */
   private handleOrganizationAssociationFilter(newDirectoryState: DirectoryBrowserBehaviourInstance, action: DirectoryNewIncomingCall): boolean {
      let newCallTriggeredDirectoryAssociations = null;
      let newIsCallTriggeredDirectoryAssociationsFilterActive = false;

      const automaticFilterOnSkillOrganisationAssociations = this.store.selectSnapshot(
         DirectorySettingsState.getAutomaticFilterOnSkillOrganisationAssociations
      )(action.brickInstanceId);
      if (automaticFilterOnSkillOrganisationAssociations) {
         const skill = this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationForCall)(action.callId);
         newCallTriggeredDirectoryAssociations = !_.isNil(skill) ? skill.directoryAssociations : null;
         newIsCallTriggeredDirectoryAssociationsFilterActive = !_.isEmpty(newCallTriggeredDirectoryAssociations);
      }

      const filterChanged =
         newDirectoryState.isCallTriggeredDirectoryAssociationsFilterActive !== newIsCallTriggeredDirectoryAssociationsFilterActive ||
         !_.isEqual(newDirectoryState.callTriggeredDirectoryAssociations, newCallTriggeredDirectoryAssociations);
      newDirectoryState.callTriggeredDirectoryAssociations = newCallTriggeredDirectoryAssociations;
      newDirectoryState.isCallTriggeredDirectoryAssociationsFilterActive = newIsCallTriggeredDirectoryAssociationsFilterActive;

      if (filterChanged) {
         this.resetSearch(newDirectoryState);
      }
      return filterChanged;
   }

   /**
    * Helper function to handle action @see DirectoryNewIncomingCall
    * in regards to setting @method DirectorySettingsState.getAutomaticRestrictForIncomingCaller
    * @param newDirectoryState Current state (to be modified while handling the action)
    * @returns true if directory has to be refreshed, else false
    */
   private handleFilterForCaller(newDirectoryState: DirectoryBrowserBehaviourInstance, action: DirectoryNewIncomingCall): boolean {
      let newFilterForSubscriberId = null;
      let newIsCallerFilterActive = false;

      const automaticRestrictForIncomingCaller = this.store.selectSnapshot(DirectorySettingsState.getAutomaticRestrictForIncomingCaller)(
         action.brickInstanceId
      );
      if (automaticRestrictForIncomingCaller) {
         newFilterForSubscriberId = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentIncomingCallDirectorySubscriberId);
         newIsCallerFilterActive = !_.isNil(newFilterForSubscriberId);
      }

      const filterChanged =
         newDirectoryState.isCallerFilterActive !== newIsCallerFilterActive || newDirectoryState.filterForSubscriberId !== newFilterForSubscriberId;
      newDirectoryState.filterForSubscriberId = newFilterForSubscriberId;
      newDirectoryState.isCallerFilterActive = newIsCallerFilterActive;

      if (filterChanged) {
         this.resetSearch(newDirectoryState);
      }
      return filterChanged;
   }

   /**
    * Helper function to handle action @see DirectoryNewIncomingCall
    * in regards to setting @method DirectorySettingsState.getClearSearchBoxOnIncomingCall
    * @param newDirectoryState Current state (to be modified while handling the action)
    * @returns true if directory has to be refreshed, else false
    */
   private handleClearSearchBoxSetting(newDirectoryState: DirectoryBrowserBehaviourInstance, action: DirectoryNewIncomingCall): boolean {
      const clearSearchBoxOnIncomingCall = this.store.selectSnapshot(DirectorySettingsState.getClearSearchBoxOnIncomingCall)(action.brickInstanceId);
      if (!clearSearchBoxOnIncomingCall) {
         return false;
      }

      const filterChanged = !_.isEmpty(newDirectoryState.searchValue) || !_.isEmpty(newDirectoryState.temporarySearchColumns);
      this.resetSearch(newDirectoryState);
      return filterChanged;
   }

   private resetSearch(newDirectoryState: DirectoryBrowserBehaviourInstance) {
      newDirectoryState.searchValue = null;
      newDirectoryState.temporarySearchColumns = [];
   }
}
